
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { Course } from "../../../domain/entities/entities";
import { dispatchError } from "../utils/error_handling";
import { wrapLoading } from "../utils/wrap_loading";
import { updateQuery } from "../utils/routing_utils";
import { getOrderFromURL } from "../utils/order_utils";
import { dispatchErrorBox, toLocaleDateTime } from "../components/mixins";
import SearchHeader from "../components/application/SearchHeader.vue";
import course from "../components/application/Course.vue";
import ModalIntroductoryContent from "../components/application/ModalIntroductoryContent.vue";
import ContentFallback from "../components/application/moldure/ContentFallback.vue";
import PaginationDefault from "../components/application/shop/PaginationDefault.vue";
import { RepositoryException } from "../../../domain/exceptions/exceptions";

export default defineComponent({
  components: {
    course,
    ModalIntroductoryContent,
    Comment,
    PaginationDefault,
    ContentFallback,
    SearchHeader,
  },

  props: {
    entityID: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const store = useStore();
    const route = useRoute();

    const courses: ComputedRef<Course[]> = computed(
      () => store.getters["globalSearch_module/instructorCourses"]
    );
    const coursesPage = computed(
      () => store.getters["globalSearch_module/instructorsCoursesPage"]
    );

    const comments = computed(() => store.getters["course_module/comments"]);

    const isCommentsModalOpen = computed(
      () => store.getters["course_module/isCommentsModalOpen"]
    );

    const openModalIntroductoryContent = ref(false);

    const commentModalTitle = ref("");

    const course = ref(new Course());

    onBeforeMount(async () => {
      await loadCourses();
    });

    async function loadCourses(): Promise<void> {
      try {
        const order = getOrderFromURL(route);
        await wrapLoading(async () => {
          await store.dispatch("globalSearch_module/getCoursesByInstructors", {
            order: order,
            instructorID: props.entityID,
          });
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    watch(
      () => route.query.page,
      async () => {
        try {
          if (route.name === "details_courses_instructors") {
            await loadCourses();
          }
        } catch (error) {
          console.error("Erro no watcher de mudança de página:", error);
        }
      }
    );

    const closeCommentsModal = async () => {
      enableBodyOverflow();
      await store.dispatch("course_module/closeCommentsModal");
      await store.dispatch("course_module/clearComments");
    };

    const enableBodyOverflow = () => {
      document.body.style.overflow = "auto";
    };

    function openIntroductoryContent(_course: Course) {
      course.value = _course;
      openModalIntroductoryContent.value = true;
    }

    function closeModalIntroductoryContent() {
      openModalIntroductoryContent.value = false;
    }

    async function loadCourseCommentsModal(course: Course) {
      try {
        commentModalTitle.value = course.title;
        await store.dispatch("course_module/openCommentsModal");
        await store.dispatch(
          "course_module/loadContentCommentsById",
          course.id
        );
      } catch (e) {
        await dispatchErrorBox(
          e instanceof RepositoryException
            ? e.message
            : "Ocorreu um erro inesperado."
        );
      }
    }

    return {
      courses,
      coursesPage,
      comments,
      isCommentsModalOpen,
      openModalIntroductoryContent,
      commentModalTitle,
      course,
      closeCommentsModal,
      openIntroductoryContent,
      closeModalIntroductoryContent,
      loadCourseCommentsModal,
      updateQuery,
      toLocaleDateTime,
    };
  },
});
